import { DragEvent, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { Editor } from 'editor'
import { BookingEngineTabs, Button, CardWrapper, EditorSidebar, Header } from 'shared/components'
import { RotateLeftIcon } from 'shared/atoms'
import {
	FLOOR_MAP_ITEM,
	PlaceItem,
	PlacementContext
} from 'shared/components/EditorSidebar/placement'
import type { FloormapConfig } from 'features/editor'
import { BOOTH_COLOR, BOX_TYPES, GRID_STEP } from 'features/floorMap/floorMap.config'
import {
	clampSize,
	getFloorSize,
	keepInside,
	resizeInside,
	snap
} from 'features/floorMap/utils/geometry'
import { useFetchMapConfig } from './queries/useFetchMapConfig'
import { useFloorMapTheme } from './hooks/useFloorMapTheme'
import { usePopulateFloorMap } from './hooks/usePopulateFloorMap'
import { useSaveFloorMapMutation } from './mutations/useSaveFloorMapMutation'

const FloorMapPage = () => {
	const { t } = useTranslation()
	const [floormapConfig, setFloormapConfig] = useState<FloormapConfig>([])
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [liveChanges, setLiveChanges] = useState<Record<string, number>>()
	const [placements] = useState(() => new Map<string, PlaceItem>())
	const theme = useFloorMapTheme()
	const { mapConfig, isMapConfigSuccess, refetchMapConfig } = useFetchMapConfig()
	const savedFloormapConfig = mapConfig?.data?.floormap_config
	const { saveFloorMap, isSaveFloorMapLoading } = useSaveFloorMapMutation()

	usePopulateFloorMap({
		isSuccess: isMapConfigSuccess,
		data: savedFloormapConfig,
		setData: setFloormapConfig
	})

	const floorSize = getFloorSize(floormapConfig)
	const selectedElement = floormapConfig.find((element) => element.id === selectedId)

	const updateElement = (id: string, changes: Record<string, number>) =>
		setFloormapConfig((config) =>
			config.map((element) =>
				element.id === id ? ({ ...element, ...changes } as FloormapConfig[number]) : element
			)
		)

	const handleDragMove = (e: any) => e.target.position(keepInside(e.target))

	const handleDragEnd = (e: any) => {
		const position = keepInside(e.target)

		e.target.position(position)
		updateElement(e.target.id(), position)
	}

	const handleTransform = (e: any) => {
		const node = e.target
		const element = floormapConfig.find((item) => item.id === node.id())

		if (!element || node.rotation()) return

		if (BOX_TYPES.includes(element.type)) setLiveChanges(resizeInside(node))

		if (element.type === 'circle') {
			const radius = resizeInside(node, true).width / 2

			setLiveChanges({ x: node.x(), y: node.y(), radius })
		}
	}

	const transformElement = (e: any) => {
		const node = e.target
		const element = floormapConfig.find((item) => item.id === node.id())

		if (!element) return

		const isCircle = element.type === 'circle'
		const x = isCircle ? node.x() : snap(node.x())
		const y = isCircle ? node.y() : snap(node.y())
		const changes = {
			x,
			y,
			rotation: node.rotation(),
			...(BOX_TYPES.includes(element.type) && {
				width: clampSize(snap(node.width()), floorSize.width - x),
				height: clampSize(snap(node.height()), floorSize.height - y)
			}),
			...(isCircle && {
				radius:
					clampSize(
						node.radius() * 2,
						Math.min(x, y, floorSize.width - x, floorSize.height - y) * 2
					) / 2
			})
		}

		node.setAttrs(changes)
		updateElement(element.id, changes)
		setLiveChanges(undefined)
	}

	const resizeSelected = (key: 'width' | 'height' | 'radius', value: number) => {
		if (selectedId) updateElement(selectedId, { [key]: value })
	}

	const deleteElement = (id: string) => {
		setFloormapConfig((config) => config.filter((element) => element.id !== id))
		if (id === selectedId) setSelectedId(null)
	}

	const handleReset = () => {
		setFloormapConfig(Array.isArray(savedFloormapConfig) ? savedFloormapConfig : [])
		setSelectedId(null)
		refetchMapConfig()
	}

	const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
		if (!event.dataTransfer.types.includes(FLOOR_MAP_ITEM)) return

		event.preventDefault()
		event.dataTransfer.dropEffect = 'copy'
	}

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		const item = event.dataTransfer.getData(FLOOR_MAP_ITEM)
		const canvas = event.currentTarget.querySelector<HTMLElement>('.konvajs-content')

		if (!item || !canvas) return

		event.preventDefault()

		const { left, top } = canvas.getBoundingClientRect()
		const x = event.clientX - left - canvas.clientLeft
		const y = event.clientY - top - canvas.clientTop

		if (x < 0 || y < 0 || x > canvas.clientWidth || y > canvas.clientHeight) return

		placements.get(item)?.({ x: snap(x), y: snap(y) })
	}

	return (
		<>
			<Header title={t('Booking engine')} tabs={<BookingEngineTabs />} />
			<div className="px-5 lg:px-0">
				<CardWrapper className="overflow-hidden">
					<header className="flex flex-col gap-1 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<h2 className="font-display text-lg font-semibold text-foreground">
								{t('Store map')}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t(
									'Floor map editor — drag areas to arrange your store, drag the corner handle to resize.'
								)}
							</p>
						</div>
						<div className="flex items-center gap-2">
							<Button
								styleType="outline"
								className="h-9 rounded-md border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
								onClick={handleReset}>
								<RotateLeftIcon className="h-[15px] w-[15px]" />
								{t('Reset')}
							</Button>
							<Button
								className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90"
								isLoading={isSaveFloorMapLoading}
								onClick={() => saveFloorMap(floormapConfig)}>
								{t('Save')}
							</Button>
						</div>
					</header>
					<PlacementContext.Provider value={placements}>
						<div className="grid gap-5 p-5 lg:grid-cols-[220px_1fr]">
							<EditorSidebar
								floormapConfig={floormapConfig}
								gridStep={GRID_STEP}
								floorSize={floorSize}
								selectedElement={
									selectedElement &&
									({ ...selectedElement, ...liveChanges } as FloormapConfig[number])
								}
								onSubmit={setFloormapConfig}
								onResize={resizeSelected}
							/>
							<div
								className="min-w-0 overflow-x-auto"
								onDragOver={handleDragOver}
								onDrop={handleDrop}>
								<Editor
									isAdmin={true}
									theme={theme}
									selectedId={selectedId}
									onSelect={setSelectedId}
									floormapConfig={floormapConfig}
									handleDragMove={handleDragMove}
									handleDragEnd={handleDragEnd}
									handleTransform={handleTransform}
									transformElement={transformElement}
									deleteElement={deleteElement}
									boothColor={BOOTH_COLOR}
									className="w-fit overflow-hidden rounded-md border-2 border-foreground/80 bg-background"
								/>
							</div>
						</div>
					</PlacementContext.Provider>
				</CardWrapper>
			</div>
		</>
	)
}

export default FloorMapPage
